import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { type ThemeMode } from '@icebreaker/shared/design';
import { readPreserved, writePreserved } from '../storage/client-storage';
import { themes } from './theme-style';

export type ThemePreference = ThemeMode | 'system';

interface ThemeContextValue {
  readonly mode: ThemeMode;
  readonly preference: ThemePreference;
  readonly theme: (typeof themes)[ThemeMode];
  readonly setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
/** BİLİNÇLİ KORUNAN anahtar: tarayıcı tercihi, kullanıcı verisi değil. */
export const THEME_STORAGE_KEY = 'wb_theme';

/**
 * PANELİN VARSAYILANI AÇIK TEMADIR — işletim sistemi tercihi TAKİP EDİLMEZ.
 *
 * Ürün uygulaması (web + mobil) `system`i izler; panel izlemez. Kullanıcı kararı
 * (02.09.2026 · §1): "ben dark mode değil normal mod istiyordum". Karanlık modda
 * çalışan bir makinede panel her açılışta koyu geliyordu ve seçme yüzeyi yoktu.
 *
 * Karanlık tema KALDIRILMADI (tasarım sistemi gereği baştan tanımlı): kabuktaki
 * tema düğmesinden seçilir ve seçim korunur. Yani varsayılan değişti, yetenek
 * değil — seçildiğinde geri gelir (md. 1c).
 */
const DEFAULT_PREFERENCE: ThemePreference = 'light';

/** İlk boyamadan ÖNCE (React ağacı kurulmadan) uygulanacak tema. */
export function initialThemeMode(): ThemeMode {
  const stored = readPreserved(THEME_STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  // `system` saklanmışsa işletim sistemi sorulur; hiçbir şey yoksa varsayılan açık.
  if (stored === 'system') {
    return typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return DEFAULT_PREFERENCE === 'light' ? 'light' : 'dark';
}

/** Karanlık tema baştan tanımlıdır (Story 0.2); panelin varsayılanı açık. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(DEFAULT_PREFERENCE);
  const [systemMode, setSystemMode] = useState<ThemeMode>('light');

  useEffect(() => {
    const stored = readPreserved(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      setPreferenceState(stored);
    }
  }, []);

  /*
   * İşletim sistemi YALNIZ tercih açıkça `system` iken dinlenir. Panelin
   * varsayılanı açık tema olduğundan bu abonelik çoğu oturumda hiç kurulmaz;
   * `matchMedia` bulunmayan ortamda (jsdom) sessizce atlanır.
   */
  useEffect(() => {
    if (preference !== 'system') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => setSystemMode(query.matches ? 'dark' : 'light');
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, [preference]);

  const mode: ThemeMode = preference === 'system' ? systemMode : preference;

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    writePreserved(THEME_STORAGE_KEY, next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, preference, theme: themes[mode], setPreference }),
    [mode, preference, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used within ThemeProvider');
  return value;
}
